import React, { useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../AuthContext";
import {
  persistSsoEnforcementForHomeOrg,
  type LogoutIntent,
} from "../ssoSession";
import { TextButton } from "@SeemplicityDev/shared";
import "./ChooseAccount.scss";

interface ChooseAccountAccount {
  id: string;
  name: string;
}

interface ChooseAccountOrganization {
  slug: string;
  name: string;
  accounts: ChooseAccountAccount[];
  /** false ⇒ SSO-enforced org. */
  password_enabled?: boolean;
}

interface ChooseAccountContextValue {
  organizations?: ChooseAccountOrganization[];
  chooseOrganizationAndAccount: (
    organization: ChooseAccountOrganization,
    account: ChooseAccountAccount
  ) => void;
  logout: (options?: LogoutIntent) => void;
  authState: {
    username?: string;
    /**
     * The CONTEXT_KEY impersonation context, set by populateAuthentication
     * before organizations load. Carries `id` only while impersonating.
     */
    context?: { id?: string };
  };
}

const ChooseAccount: React.FC = () => {
  // AuthContext is plain JS (converted in parallel); type the consumed shape locally.
  const {
    organizations,
    chooseOrganizationAndAccount,
    logout,
    authState: { username, context },
  } = useContext(AuthContext) as unknown as ChooseAccountContextValue;
  const history = useHistory();
  const select = useCallback(
    (
      organization: ChooseAccountOrganization,
      account: ChooseAccountAccount
    ) => {
      // A multi-account user lands here on EVERY login (clearOrgAndAccount
      // wipes the stored org/account on logout), so this is the only place the
      // enforced hint gets persisted for them. The helper skips the write while
      // impersonating.
      persistSsoEnforcementForHomeOrg(organization, context);
      chooseOrganizationAndAccount(organization, account);
      history.push("/dashboard");
    },
    [chooseOrganizationAndAccount, context, history]
  );
  const { t } = useTranslation();

  if (!organizations) {
    return null;
  }

  if (organizations.length === 0) {
    throw new Error("Unable to login, please try again in a few moments.");
  }

  return (
    <div className="accounts-page-body">
      {organizations.map((org) => (
        <div key={org.slug} className="organization">
          <h2>{org.name}</h2>
          <p className="text-medium">Choose an Account</p>
          <div className="organization-items">
            {org.accounts.map((account) => (
              <TextButton
                key={account.id}
                className="account-button"
                leftIcon="seem-icon-user"
                onClick={() => {
                  select(org, account);
                }}
                label={account.name}
                size="xl"
                isSquare={true}
                isFullWidth={true}
              />
            ))}
          </div>
        </div>
      ))}
      <p className="text-medium">
        {t("Logged in as")} {username}
      </p>
      <p>
        {t("Not you?")}{" "}
        <TextButton
          type={"button"}
          buttonStyle={"textLink"}
          // Wrapped: TextButton hands its handler a MouseEvent, which is not a
          // LogoutIntent. Calling logout() bare keeps the default (deliberate).
          onClick={() => logout()}
          label={t("Logout")}
        />
      </p>
    </div>
  );
};

export default ChooseAccount;
